import React, { useState, useEffect } from 'react';
import { formatDate } from '@/utils/dateFormat';
import { Search, Package, CheckCircle, Clock, AlertTriangle, Phone, Mail, QrCode, Share2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

// Public API endpoint (doesn't require auth)
const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

export default function ConsultaClientePage() {
  const [searchType, setSearchType] = useState('cpf'); // cpf, telefone, ciclo
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedCiclo, setSelectedCiclo] = useState(null);

  // Auto-search from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cicloParam = urlParams.get('ciclo');
    const cpfParam = urlParams.get('cpf');
    const telefoneParam = urlParams.get('telefone');

    if (cicloParam) {
      setSearchType('ciclo');
      setSearchValue(cicloParam);
      // Trigger search
      setTimeout(() => {
        document.getElementById('search-btn')?.click();
      }, 500);
    } else if (cpfParam) {
      setSearchType('cpf');
      setSearchValue(cpfParam);
      setTimeout(() => {
        document.getElementById('search-btn')?.click();
      }, 500);
    } else if (telefoneParam) {
      setSearchType('telefone');
      setSearchValue(telefoneParam);
      setTimeout(() => {
        document.getElementById('search-btn')?.click();
      }, 500);
    }
  }, []);

  const generateQRUrl = (numeroCiclo) => {
    return `${FRONTEND_URL}/consulta-cliente?ciclo=${encodeURIComponent(numeroCiclo)}`;
  };

  const handleShowQR = (ciclo) => {
    setSelectedCiclo(ciclo);
    setShowQRDialog(true);
  };

  const handleCopyLink = (numeroCiclo) => {
    const url = generateQRUrl(numeroCiclo);
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  const handleShare = async (ciclo) => {
    const url = generateQRUrl(ciclo.numero_ciclo);
    const text = `Consulte seu material esterilizado - Ciclo ${ciclo.numero_ciclo}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Sterisul - Consulta', text, url });
      } catch (err) {
        handleCopyLink(ciclo.numero_ciclo);
      }
    } else {
      handleCopyLink(ciclo.numero_ciclo);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) {
      toast.error('Digite um valor para buscar');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const params = new URLSearchParams();
      params.append(searchType, searchValue.replace(/\D/g, ''));
      
      const response = await fetch(`${API_URL}/api/consulta-publica?${params.toString()}`);
      const data = await response.json();

      if (response.ok) {
        setResults(data);
        if (data.length === 0) {
          toast.info('Nenhum material encontrado');
        }
      } else {
        setError(data.detail || 'Erro ao buscar');
        toast.error(data.detail || 'Erro ao buscar');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'em_processo':
        return { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Em Processo' };
      case 'aprovado':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Aprovado' };
      case 'aguardando_retirada':
        return { icon: Package, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Pronto para Retirada' };
      case 'retirado':
        return { icon: CheckCircle, color: 'text-slate-600', bg: 'bg-slate-100', label: 'Retirado' };
      default:
        return { icon: Clock, color: 'text-slate-600', bg: 'bg-slate-100', label: status };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-blue-600 text-white py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <img 
            src="/sterisul-logo.png" 
            alt="Sterisul" 
            className="h-16 mx-auto mb-4 bg-white rounded-lg p-2"
            onError={(e) => e.target.style.display = 'none'}
          />
          <h1 className="text-2xl font-bold mb-2">Consulta de Materiais</h1>
          <p className="text-blue-100">Verifique o status dos seus materiais esterilizados</p>
        </div>
      </div>

      {/* Search Form */}
      <div className="max-w-2xl mx-auto px-4 -mt-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <form onSubmit={handleSearch}>
            {/* Search Type Selector */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setSearchType('cpf')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  searchType === 'cpf' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                CPF/CNPJ
              </button>
              <button
                type="button"
                onClick={() => setSearchType('telefone')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  searchType === 'telefone' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Telefone
              </button>
              <button
                type="button"
                onClick={() => setSearchType('ciclo')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  searchType === 'ciclo' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Nº Ciclo
              </button>
            </div>

            {/* Search Input */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={
                    searchType === 'cpf' ? 'Digite seu CPF ou CNPJ' :
                    searchType === 'telefone' ? 'Digite seu telefone' :
                    'Digite o número do ciclo'
                  }
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="pl-10 h-12 text-lg"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-12 px-6 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                id="search-btn"
                data-testid="search-btn"
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>
          </form>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="max-w-2xl mx-auto px-4 mt-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            Resultados ({results.length})
          </h2>

          {results.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-center">
              <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">Nenhum material encontrado</p>
              <p className="text-sm text-slate-500 mt-2">
                Verifique os dados informados e tente novamente
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((item, index) => {
                const statusInfo = getStatusInfo(item.status);
                const StatusIcon = statusInfo.icon;

                return (
                  <div 
                    key={index} 
                    className="bg-white rounded-xl shadow p-4 hover:shadow-md transition-shadow"
                    data-testid={`result-item-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                            <StatusIcon className="w-4 h-4 inline mr-1" />
                            {statusInfo.label}
                          </span>
                        </div>
                        
                        <h3 className="font-semibold text-slate-900">
                          Ciclo: {item.numero_ciclo}
                        </h3>
                        
                        <div className="mt-2 space-y-1 text-sm text-slate-600">
                          <p>
                            <span className="text-slate-500">Materiais:</span>{' '}
                            {item.materiais?.map(m => m.nome).join(', ') || 'N/A'}
                          </p>
                          <p>
                            <span className="text-slate-500">Data:</span>{' '}
                            {item.data_hora ? formatDate(item.data_hora) : 'N/A'}
                          </p>
                          {item.validade && (
                            <p>
                              <span className="text-slate-500">Validade:</span>{' '}
                              {formatDate(item.validade)}
                            </p>
                          )}
                        </div>
                        
                        {/* Sharing buttons */}
                        <div className="mt-3 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleShare(item)}
                            className="flex items-center gap-1 text-xs"
                          >
                            <Share2 className="w-3 h-3" />
                            Compartilhar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyLink(item.numero_ciclo)}
                            className="flex items-center gap-1 text-xs"
                          >
                            <Copy className="w-3 h-3" />
                            Copiar Link
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleShowQR(item)}
                            className="flex items-center gap-1 text-xs"
                          >
                            <QrCode className="w-3 h-3" />
                            QR Code
                          </Button>
                        </div>
                      </div>

                      {item.status === 'aguardando_retirada' && (
                        <div className="ml-4 p-3 bg-amber-50 rounded-lg text-center">
                          <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                          <span className="text-xs font-medium text-amber-700">
                            Retirar
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="max-w-2xl mx-auto px-4 py-8 mt-8">
        <div className="bg-slate-50 rounded-xl p-6 text-center">
          <h3 className="font-semibold text-slate-900 mb-4">Sterisul - Centro de Esterilização</h3>
          <div className="flex flex-col sm:flex-row justify-center gap-4 text-sm text-slate-600">
            <a href="tel:+5500000000000" className="flex items-center justify-center gap-2 hover:text-blue-600">
              <Phone className="w-4 h-4" />
              (00) 0000-0000
            </a>
            <a href="mailto:contato@sterisul.com.br" className="flex items-center justify-center gap-2 hover:text-blue-600">
              <Mail className="w-4 h-4" />
              contato@sterisul.com.br
            </a>
          </div>
        </div>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code - Ciclo {selectedCiclo?.numero_ciclo}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 py-4">
            {selectedCiclo && (
              <>
                <div className="bg-white p-4 rounded-lg border">
                  <QRCodeSVG 
                    value={generateQRUrl(selectedCiclo.numero_ciclo)}
                    size={200}
                    level="M"
                    includeMargin={true}
                  />
                </div>
                <div className="text-center space-y-2">
                  <p className="text-sm text-slate-600">
                    Escaneie para acessar diretamente
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyLink(selectedCiclo.numero_ciclo)}
                      className="flex items-center gap-1"
                    >
                      <Copy className="w-4 h-4" />
                      Copiar Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShare(selectedCiclo)}
                      className="flex items-center gap-1"
                    >
                      <Share2 className="w-4 h-4" />
                      Compartilhar
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
